import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@environment';
import { EventsFacade } from '@presentationStore/events';
import { EventItem, SentryLoggerService } from '@shared';
import { Observable, of } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';
import { EventsApiService } from './events.api.service';

@Injectable({
  providedIn: 'root',
})
export class EventsService {
  private readonly http: HttpClient = inject(HttpClient);
  private eventsFacade: EventsFacade = inject(EventsFacade);
  private eventsApi: EventsApiService = inject(EventsApiService);
  private sentryLogger: SentryLoggerService = inject(SentryLoggerService);

  private allUpcomingEventsSigInternal: Signal<EventItem[] | undefined> = toSignal(this.eventsFacade.upcomingEvents$);
  private publicUpcomingEventsSigInternal: Signal<EventItem[] | undefined> = toSignal(this.eventsFacade.publicUpcomingEvents$);
  private inPreparationEventsSigInternal: Signal<EventItem[] | undefined> = toSignal(this.eventsFacade.publicInPreparationEvents$);
  private pastEventsSigInternal: Signal<EventItem[] | undefined> = toSignal(this.eventsFacade.pastEvents$);
  private _eventsLoadingSig: Signal<boolean | undefined> = toSignal(this.eventsFacade.loading$);
  private _loadingMoreUpcomingEventsSig: Signal<boolean | undefined> = toSignal(this.eventsFacade.loadingMoreUpcomingEvents$);
  private _loadingMoreInPreparationEventsSig: Signal<boolean | undefined> = toSignal(this.eventsFacade.loadingMoreInPreparationEvents$);
  private _loadingMorePastEventsSig: Signal<boolean | undefined> = toSignal(this.eventsFacade.loadingMorePastEvents$);
  private _hasMoreUpcomingEventsSig: Signal<boolean | undefined> = toSignal(this.eventsFacade.hasMoreUpcomingEvents$);
  private _hasMoreInPreparationEventsSig: Signal<boolean | undefined> = toSignal(this.eventsFacade.hasMoreInPreparationEvents$);
  private _hasMorePastEventsSig: Signal<boolean | undefined> = toSignal(this.eventsFacade.hasMorePastEvents$);

  eventsLoadingSig: Signal<boolean> = computed(() => this._eventsLoadingSig() ?? false);
  loadingMoreUpcomingEventsSig: Signal<boolean> = computed(() => this._loadingMoreUpcomingEventsSig() ?? false);
  loadingMoreInPreparationEventsSig: Signal<boolean> = computed(() => this._loadingMoreInPreparationEventsSig() ?? false);
  loadingMorePastEventsSig: Signal<boolean> = computed(() => this._loadingMorePastEventsSig() ?? false);
  hasMoreUpcomingEventsSig: Signal<boolean> = computed(() => this._hasMoreUpcomingEventsSig() ?? false);
  hasMoreInPreparationEventsSig: Signal<boolean> = computed(() => this._hasMoreInPreparationEventsSig() ?? false);
  hasMorePastEventsSig: Signal<boolean> = computed(() => this._hasMorePastEventsSig() ?? false);

  pastEventsSig: Signal<EventItem[]> = computed(() => [ ...(this.pastEventsSigInternal() ?? []) ]);

  upcomingEvents: Signal<EventItem[]> = computed(() => [ ...(this.publicUpcomingEventsSigInternal() ?? []) ]);

  inPreparationEvents: Signal<EventItem[]> = computed(() => [ ...(this.inPreparationEventsSigInternal() ?? []) ]);

  upcomingEventsWithPrivates: Signal<EventItem[]> = computed(() => [ ...(this.allUpcomingEventsSigInternal() ?? []) ]);

  nextEvent: Signal<EventItem | null> = computed(() => {
    const events = this.upcomingEvents();
    return events.length > 0 ? events[0] : null;
  });

  findEventInStoreBySlug(slug: string | null): EventItem | null {
    if (!slug) {
      return null;
    }

    const upcoming = this.allUpcomingEventsSigInternal() ?? [];
    const foundUpcoming = upcoming.find((event) => event.slug === slug);
    if (foundUpcoming) {
      return foundUpcoming;
    }

    const past = this.pastEventsSigInternal() ?? [];
    return past.find((event) => event.slug === slug) ?? null;
  }

  fetchEventBySlug(slug: string): Observable<EventItem> {
    return this.eventsApi.getEventBySlug(slug);
  }

  getEventDetailState(slug: string | null): Observable<{ event: EventItem | null; loading: boolean }> {
    if (!slug) {
      return of({ event: null, loading: false });
    }

    const eventFromStore = this.findEventInStoreBySlug(slug);
    if (eventFromStore) {
      return of({ event: eventFromStore, loading: false });
    }

    return this.fetchEventBySlug(slug).pipe(
      map((event) => ({ event, loading: false })),
      startWith({ event: null, loading: true }),
      catchError(() => of({ event: null, loading: false })),
    );
  }

  loadMorePastEvents(search?: string, tagIds?: string[], dateFrom?: string, dateTo?: string): void {
    this.eventsFacade.loadMorePastEvents(search, tagIds, dateFrom, dateTo);
  }

  loadMoreUpcomingEvents(search?: string, tagIds?: string[], dateFrom?: string, dateTo?: string): void {
    this.eventsFacade.loadMoreUpcomingEvents(search, tagIds, dateFrom, dateTo);
  }

  loadMoreInPreparationEvents(search?: string, tagIds?: string[], dateFrom?: string, dateTo?: string): void {
    this.eventsFacade.loadMoreInPreparationEvents(search, tagIds, dateFrom, dateTo);
  }

  loadUpcomingEvents(page: number = 1, search?: string, tagIds?: string[], dateFrom?: string, dateTo?: string): void {
    this.eventsFacade.loadUpcomingEvents(page, search, tagIds, dateFrom, dateTo);
  }

  loadPastEvents(page: number = 1, search?: string, tagIds?: string[], dateFrom?: string, dateTo?: string): void {
    this.eventsFacade.loadPastEvents(page, search, tagIds, dateFrom, dateTo);
  }

  loadInPreparationEvents(page: number = 1, search?: string, tagIds?: string[], dateFrom?: string, dateTo?: string): void {
    this.eventsFacade.loadInPreparationEvents(page, search, tagIds, dateFrom, dateTo);
  }

  test(): void {
    this.http.get(environment.apiUrl + '/status').subscribe({
      next: (res) => {
        console.log(res);
      },
      error: (error) => {
        console.error('API test failed:', error);
        this.sentryLogger.captureHttpError(error, {
          component: 'EventsService',
          action: 'test',
          extra: {
            endpoint: environment.apiUrl + '/status',
          },
        });
      },
    });
  }
}
